import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function CampsBayPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    fromDate: '',
    toDate: '',
    location: 'Camps Bay',
    depositAvailable: ''
  });

  const [depositAmount, setDepositAmount] = useState(0);
  const [isLocationsOpen, setIsLocationsOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (formData.fromDate && formData.toDate) {
      const fromDate = new Date(formData.fromDate);
      const toDate = new Date(formData.toDate);
      const timeDiff = toDate.getTime() - fromDate.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));
      
      if (daysDiff > 0) {
        const calculatedDeposit = daysDiff * 3000;
        setDepositAmount(calculatedDeposit);
      } else {
        setDepositAmount(0);
      }
    } else {
      setDepositAmount(0);
    }
  }, [formData.fromDate, formData.toDate]);

  // Add structured data for SEO
  useEffect(() => {
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "name": "Beach Bugs Camps Bay",
      "description": "Premium beach buggy rental service in Camps Bay, Cape Town, South Africa",
      "url": import.meta.env.VITE_SITE_URL || "https://example.com",
      "telephone": "+27-XXX-XXX-XXXX",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Camps Bay",
        "addressRegion": "Western Cape",
        "addressCountry": "ZA"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": -33.9553,
        "longitude": 18.3756
      },
      "openingHours": "Mo-Su 08:00-18:00",
      "priceRange": "R3000-R21000",
      "serviceArea": {
        "@type": "GeoCircle",
        "geoMidpoint": {
          "@type": "GeoCoordinates",
          "latitude": -33.9553,
          "longitude": 18.3756
        },
        "geoRadius": "50000"
      }
    };

    const faqData = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What makes Camps Bay perfect for beach buggy adventures?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Camps Bay offers stunning Atlantic Ocean views, pristine white sand beaches, and perfect terrain for beach buggy adventures with spectacular Twelve Apostles mountain backdrop."
          }
        },
        {
          "@type": "Question",
          "name": "What is the daily rate for beach buggy rental in Camps Bay?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our beach buggy rental rate is R3,000 per day with a minimum rental period of 2 days in Camps Bay."
          }
        },
        {
          "@type": "Question",
          "name": "Is insurance included for Camps Bay rentals?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes, all our beach buggy rentals in Camps Bay include comprehensive insurance coverage up to R40,000."
          }
        }
      ]
    };

    // Remove existing structured data
    const existingScripts = document.querySelectorAll('script[type="application/ld+json"]');
    existingScripts.forEach(script => script.remove());

    // Add new structured data
    const script1 = document.createElement('script');
    script1.type = 'application/ld+json';
    script1.textContent = JSON.stringify(structuredData);
    document.head.appendChild(script1);

    const script2 = document.createElement('script');
    script2.type = 'application/ld+json';
    script2.textContent = JSON.stringify(faqData);
    document.head.appendChild(script2);

    return () => {
      const scripts = document.querySelectorAll('script[type="application/ld+json"]');
      scripts.forEach(script => script.remove());
    };
  }, []);

  // Add SEO meta tags for this specific page
  useEffect(() => {
    // Update page title and meta tags
    document.title = "Beach Buggy Rental Camps Bay Cape Town | Beach Bugs";
    
    // Update meta description
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Premium beach buggy rental in Camps Bay with stunning Twelve Apostles views. R3,000/day with insurance included. Book your Camps Bay beach buggy adventure today!');
    }
    
    // Update meta keywords
    const metaKeywords = document.querySelector('meta[name="keywords"]');
    if (metaKeywords) {
      metaKeywords.setAttribute('content', 'beach buggy rental Camps Bay, Camps Bay beach buggy, Cape Town beach buggy, Twelve Apostles views');
    }
    
    // Update canonical URL
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink) {
      canonicalLink.setAttribute('href', 'https://example.com/camps-bay');
    }
    
    // Update geographic meta tags
    const geoRegion = document.querySelector('meta[name="geo.region"]');
    if (geoRegion) {
      geoRegion.setAttribute('content', 'ZA-WC');
    }
    
    const geoPlacename = document.querySelector('meta[name="geo.placename"]');
    if (geoPlacename) {
      geoPlacename.setAttribute('content', 'Camps Bay, Cape Town');
    }
    
    const geoPosition = document.querySelector('meta[name="geo.position"]');
    if (geoPosition) {
      geoPosition.setAttribute('content', '-33.9553;18.3756');
    }
    
    // Update Open Graph tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', 'Beach Buggy Rental Camps Bay Cape Town | Beach Bugs');
    }
    
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) {
      ogDescription.setAttribute('content', 'Premium beach buggy rental in Camps Bay with stunning Twelve Apostles views. R3,000/day with insurance included. Book your Camps Bay beach buggy adventure today!');
    }
    
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) {
      ogUrl.setAttribute('content', 'https://example.com/camps-bay');
    }
  }, []);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Calculate rental days and total amount
    const fromDate = new Date(formData.fromDate);
    const toDate = new Date(formData.toDate);
    const timeDiff = toDate.getTime() - fromDate.getTime();
    const rentalDays = Math.ceil(timeDiff / (1000 * 3600 * 24));
    const totalAmount = rentalDays * 3000;
    
    const formDataToSubmit = new FormData();
    Object.entries(formData).forEach(([key, value]) => {
      formDataToSubmit.append(key, value);
    });
    
    // Add calculated values to the form submission
    formDataToSubmit.append('rentalDays', rentalDays.toString());
    formDataToSubmit.append('totalAmount', `R${totalAmount.toLocaleString()}`);
    formDataToSubmit.append('dailyRate', 'R3,000');

    try {
      const response = await fetch('https://readdy.ai/api/form/d4326o1eds0ugd973hmg', {
        method: 'POST',
        body: formDataToSubmit
      });
      
      if (response.ok) {
        alert('Thank you, we\'ll be in contact IF we have a beach buggy available. They are all booked out 99% of the time. Thank you for understanding.');
        setFormData({
          name: '',
          email: '',
          phone: '',
          fromDate: '',
          toDate: '',
          location: 'Camps Bay',
          depositAvailable: ''
        });
      } else {
        alert('There was an error submitting your request. Please try again.');
      }
    } catch (error) {
      alert('There was an error submitting your request. Please try again.');
    }
  };

  const features = [
    { icon: 'ri-car-line', title: 'Reliable Beach Buggies', description: 'Top-quality vehicles for ultimate adventures' },
    { icon: 'ri-emotion-happy-line', title: 'Unforgettable Experiences', description: 'Create memories that last a lifetime' },
    { icon: 'ri-smartphone-line', title: 'Instant Price Check', description: 'Check rates instantly with our smart system' },
    { icon: 'ri-time-line', title: 'Flexible Rentals', description: '2 days minimum @ R3,000 per day' }
  ];

  const nearbyLocations = [
    'Bakoven', 'Clifton', 'Sea Point', 
    'Bantry Bay', 'Fresnaye', 'Green Point'
  ];

  const steps = [
    { icon: 'ri-file-text-line', title: 'Complete The Form', description: 'Quick and easy booking process' },
    { icon: 'ri-price-tag-3-line', title: 'Get Instant Quote', description: 'Receive rates immediately' },
    { icon: 'ri-emotion-laugh-line', title: 'Start Your Adventure', description: 'Hit the beach and create memories' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-md shadow-sm sticky top-0 z-50 border-b border-gray-100">
        <nav className="mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20 sm:h-20">
            <div className="flex items-center space-x-3 min-w-0 flex-shrink-0">
              <a className="flex items-center space-x-3" href="/" data-discover="true">
                <img
                  src="https://static.readdy.ai/image/5c2d16bce98820aab16a3b52f2f5cfd8/71f081b0c967dd005308d1aa03cdf9ab.png"
                  alt="Beach Bugs Logo"
                  className="w-[180px] sm:w-[250px] h-auto object-contain flex-shrink-0"
                />
              </a>
            </div>
            <div className="hidden lg:block flex-1 max-w-md mx-8">
              <div className="flex items-center justify-center space-x-8">
                <a className="text-base font-medium transition-all duration-300 whitespace-nowrap cursor-pointer text-gray-600 hover:text-[#e9f638] relative group" href="/" data-discover="true">
                  Home
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#e9f638] transition-all duration-300 group-hover:w-full"></span>
                </a>
                <div className="relative">
                  <button 
                    className="text-base font-medium transition-all duration-300 whitespace-nowrap cursor-pointer text-black hover:text-[#e9f638] relative group flex items-center"
                    onClick={() => setIsLocationsOpen(!isLocationsOpen)}
                  >
                    Locations
                    <i className={`ri-arrow-down-s-line ml-1 transition-transform duration-300 ${isLocationsOpen ? 'rotate-180' : ''}`}></i>
                    <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#e9f638] transition-all duration-300 group-hover:w-full"></span>
                  </button>
                  {isLocationsOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
                      <a href="/bloubergstrand" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Bloubergstrand
                      </a>
                      <a href="/melkbosstrand" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Melkbosstrand
                      </a>
                      <a href="/camps-bay" className="block px-4 py-2 text-sm text-yellow-500 font-medium">
                        Camps Bay
                      </a>
                      <a href="/green-point" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Green Point
                      </a>
                      <a href="/sea-point" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Sea Point
                      </a>
                      <a href="/table-view" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Table View
                      </a>
                      <a href="/stilbaai" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Stilbaai
                      </a>
                      <a href="/jeffreys-bay" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Jeffreys Bay
                      </a>
                      <a href="/wilderness" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Wilderness
                      </a>
                      <a href="/george" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        George
                      </a>
                      <a href="/langebaan" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        Langebaan
                      </a>
                    </div>
                  )}
                </div>
                <a className="text-base font-medium transition-all duration-300 whitespace-nowrap cursor-pointer text-gray-600 hover:text-[#e9f638] relative group" href="/book" data-discover="true">
                  Book Now
                  <span className="absolute -bottom-1 left-0 w-0 h-0.5 bg-[#e9f638] transition-all duration-300 group-hover:w-full"></span>
                </a>
              </div>
            </div>
            <div className="hidden lg:block flex-shrink-0">
              <a className="bg-[#e9f638] text-black px-4 xl:px-6 py-2 xl:py-3 rounded-full text-sm font-semibold hover:bg-[#d4e032] transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:-translate-y-0.5" href="/book" data-discover="true">Book a Bug!</a>
            </div>
            <div className="lg:hidden flex-shrink-0">
              <button 
                className="inline-flex items-center justify-center p-2 rounded-xl text-gray-600 hover:text-black hover:bg-gray-100 cursor-pointer transition-all duration-300"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              >
                <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-xl sm:text-2xl transition-all duration-300`}></i>
              </button>
            </div>
          </div>
          
          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <div className="lg:hidden absolute top-full left-0 right-0 bg-white border-t border-gray-100 shadow-xl z-40">
              <div className="px-4 py-6 space-y-4">
                <a 
                  className="block text-lg font-medium text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2" 
                  href="/" 
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Home
                </a>
                <a 
                  className="block text-lg font-medium text-gray-600 hover:text-[#e9f638] transition-colors duration-300 py-2" 
                  href="/book" 
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Book Now
                </a>
                <div className="space-y-2">
                  <div className="text-lg font-medium text-gray-600 py-2">Locations</div>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/bloubergstrand"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Bloubergstrand
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/melkbosstrand"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Melkbosstrand
                  </a>
                  <a 
                    className="block text-base text-[#e9f638] bg-gray-50 font-medium py-2 pl-4 rounded" 
                    href="/camps-bay"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-fill mr-2 text-[#e9f638]"></i>
                    Camps Bay
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/green-point"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Green Point
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/sea-point"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Sea Point
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/table-view"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Table View
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/stilbaai"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Stilbaai
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/jeffreys-bay"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Jeffreys Bay
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/wilderness"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Wilderness
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/george"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    George
                  </a>
                  <a 
                    className="block text-base text-gray-700 hover:text-[#e9f638] transition-colors duration-300 py-2 pl-4" 
                    href="/langebaan"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <i className="ri-map-pin-line mr-2 text-[#e9f638]"></i>
                    Langebaan
                  </a>
                </div>
                <div className="pt-4">
                  <a 
                    className="block w-full bg-[#e9f638] text-black px-6 py-3 rounded-full text-lg font-semibold hover:bg-[#d4e032] transition-all duration-300 text-center shadow-lg" 
                    href="/book"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    Book a Bug!
                  </a>
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      <main className="flex-1">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-gray-900 via-black to-gray-800 text-white py-12 sm:py-16 lg:py-20 min-h-screen flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img 
              alt="Matte black beach buggy with two blonde girls driving on coastal road" 
              className="w-full h-full object-cover object-top" 
              src="https://public.readdy.ai/ai/img_res/728010cc68acd8504ad0a6bf70631726.jpg"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
          </div>
          
          {/* Animated background elements */}
          <div className="absolute top-20 right-20 w-16 sm:w-32 h-16 sm:h-32 bg-blue-400/20 rounded-full blur-xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-12 sm:w-24 h-12 sm:h-24 bg-blue-400/20 rounded-full blur-xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 right-1/3 w-8 sm:w-16 h-8 sm:h-16 bg-blue-400/10 rounded-full blur-lg animate-bounce"></div>
          
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-5xl">
              <div className="space-y-6 sm:space-y-8 lg:space-y-10">
                <div className="space-y-6 sm:space-y-8">
                  <div className="space-y-4">
                    <div className="inline-flex items-center px-3 sm:px-4 py-2 bg-black/80 backdrop-blur-sm rounded-full border border-[#e9f638]/30">
                      <i className="ri-star-fill text-[#e9f638] mr-2 text-sm sm:text-base"></i>
                      <span className="text-white font-medium text-sm sm:text-base">South Africa's #1 Beach Buggy Rental</span>
                    </div>
                    <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl font-bold leading-tight">
                      Rent a <span className="bg-gradient-to-r from-[#e9f638] to-[#d4e032] bg-clip-text text-transparent text-6xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-9xl">BEACH BUGGY!</span>
                    </h1>
                  </div>
                  <div className="space-y-4 sm:space-y-6">
                    <div className="flex flex-col sm:flex-row sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
                      <p className="text-3xl sm:text-4xl lg:text-5xl bg-gradient-to-r from-[#e9f638] to-[#d4e032] bg-clip-text text-transparent font-bold">R3,000</p>
                      <div className="text-lg sm:text-xl lg:text-2xl text-gray-300">/day</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Nearby Locations */}
        <section className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-16">
              <div className="inline-flex items-center px-3 sm:px-4 py-2 bg-black rounded-full mb-4 sm:mb-6">
                <i className="ri-map-pin-line text-[#e9f638] mr-2"></i>
                <span className="text-white font-medium text-sm sm:text-base">Nearby Areas</span>
              </div>
              <h3 className="text-3xl sm:text-4xl font-bold text-black mb-4 sm:mb-6">Explore Camps Bay & Surrounds</h3>
              <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">Discover the best coastal locations around Camps Bay with our beach buggies</p>
            </div>
            <div className="flex flex-wrap justify-center gap-3 sm:gap-4">
              {nearbyLocations.map((location, index) => (
                <div key={index} className="bg-white px-4 sm:px-6 lg:px-8 py-3 sm:py-4 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-100 hover:border-[#e9f638]/30 group hover:-translate-y-1">
                  <span className="text-gray-700 font-medium group-hover:text-[#e9f638] transition-colors duration-300 text-sm sm:text-base whitespace-nowrap">{location}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Main Content Section */}
        <section id="booking" className="py-16 sm:py-20 lg:py-28 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 sm:gap-16 lg:gap-20 items-center">
              {/* Left Side - Features */}
              <div className="space-y-8 sm:space-y-10">
                <div className="space-y-4 sm:space-y-6">
                  <div className="inline-flex items-center px-4 py-2 bg-black rounded-full mb-4">
                    <i className="ri-award-line text-[#e9f638] mr-2"></i>
                    <span className="text-white font-medium text-sm sm:text-base">Why Choose Us</span>
                  </div>
                  <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black leading-tight">Why Choose Beach Bugs?</h3>
                  <p className="text-lg sm:text-xl text-gray-600 leading-relaxed">Beach buggy rental with everything included for your perfect coastal adventure.</p>
                </div>
                
                <div className="space-y-6 sm:space-y-8">
                  {features.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-4 sm:space-x-6 group">
                      <div className="w-12 h-12 sm:w-14 sm:h-14 bg-gradient-to-br from-[#e9f638] to-[#d4e032] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-110">
                        <i className={`${feature.icon} text-black text-lg sm:text-xl`}></i>
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-lg sm:text-xl font-bold text-black mb-2 group-hover:text-[#e9f638] transition-colors duration-300">{feature.title}</h4>
                        <p className="text-gray-600 leading-relaxed text-sm sm:text-base">{feature.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="pt-6 sm:pt-8">
                  <div className="relative group">
                    <img 
                      src="https://static.readdy.ai/image/5c2d16bce98820aab16a3b52f2f5cfd8/ad931f38bf6c5b1dce14cbe89e0b3d03.png" 
                      alt="Beach buggy rental Camps Bay" 
                      title="Premium beach buggy rental Camps Bay Twelve Apostles views"
                      className="w-full max-w-md rounded-2xl shadow-2xl group-hover:shadow-3xl transition-all duration-500 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                </div>
              </div>

              {/* Right Side - Booking Form */}
              <div className="bg-gradient-to-br from-gray-50 to-white rounded-3xl p-6 sm:p-8 lg:p-10 shadow-2xl border border-gray-100">
                <div className="text-center mb-8 sm:mb-10">
                  <div className="inline-flex items-center px-4 py-2 bg-black rounded-full mb-4">
                    <i className="ri-calendar-check-line text-[#e9f638] mr-2"></i>
                    <span className="text-white font-medium text-sm sm:text-base">Quick Booking</span>
                  </div>
                  <h4 className="text-2xl sm:text-3xl font-bold text-black mb-3">Book a Bug!</h4>
                  <p className="text-gray-600 text-sm sm:text-base">Fill out the form below to check rates and availability</p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-5 sm:space-y-6" data-readdy-form>
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 sm:px-5 py-3 sm:py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#e9f638] focus:border-transparent text-sm transition-all duration-300 hover:border-gray-300"
                      placeholder="Your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Email *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 sm:px-5 py-3 sm:py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#e9f638] focus:border-transparent text-sm transition-all duration-300 hover:border-gray-300"
                      placeholder="your@email.com"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Telephone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 sm:px-5 py-3 sm:py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#e9f638] focus:border-transparent text-sm transition-all duration-300 hover:border-gray-300"
                      placeholder="Your phone number"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="fromDate" className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">From *</label>
                      <input
                        type="date"
                        id="fromDate"
                        name="fromDate"
                        value={formData.fromDate}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 sm:px-5 py-3 sm:py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#e9f638] focus:border-transparent text-sm transition-all duration-300 hover:border-gray-300"
                      />
                    </div>
                    <div>
                      <label htmlFor="toDate" className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">To *</label>
                      <input
                        type="date"
                        id="toDate"
                        name="toDate"
                        value={formData.toDate}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 sm:px-5 py-3 sm:py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#e9f638] focus:border-transparent text-sm transition-all duration-300 hover:border-gray-300"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="location" className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">Where do you need it? *</label>
                    <input
                      type="text"
                      id="location"
                      name="location"
                      value={formData.location}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 sm:px-5 py-3 sm:py-4 border border-gray-200 rounded-xl focus:ring-2 focus:ring-[#e9f638] focus:border-transparent text-sm transition-all duration-300 hover:border-gray-300"
                      placeholder="Delivery location"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2 sm:mb-3">
                      {depositAmount > 0 ? `Do you have R${depositAmount.toLocaleString()} available? *` : 'Do you have the calculated amount available? *'}
                    </label>
                    {depositAmount > 0 && (
                      <p className="text-xs text-gray-500 mb-3">
                        Calculation: {Math.ceil((new Date(formData.toDate).getTime() - new Date(formData.fromDate).getTime()) / (1000 * 3600 * 24))} days × R3,000 = R{depositAmount.toLocaleString()}
                      </p>
                    )}
                    <div className="space-y-3">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="depositAvailable"
                          value="yes"
                          checked={formData.depositAvailable === 'yes'}
                          onChange={handleInputChange}
                          required
                          className="mr-3 text-[#e9f638] focus:ring-[#e9f638]"
                        />
                        <span className="text-sm text-gray-700">Yes, I have the amount available</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="depositAvailable"
                          value="no"
                          checked={formData.depositAvailable === 'no'}
                          onChange={handleInputChange}
                          required
                          className="mr-3 text-[#e9f638] focus:ring-[#e9f638]"
                        />
                        <span className="text-sm text-gray-700">No, I don't have the amount available</span>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-[#e9f638] text-black py-4 sm:py-5 rounded-xl text-base sm:text-lg font-bold hover:bg-[#d4e032] transition-all duration-300 whitespace-nowrap cursor-pointer shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                  >
                    Check rates & availability
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-16 sm:py-20 lg:py-28 bg-gradient-to-b from-gray-50 to-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12 sm:mb-16 lg:mb-20">
              <div className="inline-flex items-center px-4 py-2 bg-black rounded-full mb-4 sm:mb-6">
                <i className="ri-lightbulb-line text-[#e9f638] mr-2"></i>
                <span className="text-white font-medium text-sm sm:text-base">Simple Process</span>
              </div>
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-black mb-6 sm:mb-8">How It Works</h3>
              <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">Three simple steps to your ultimate beach adventure</p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10 lg:gap-12">
              {steps.map((step, index) => (
                <div key={index} className="text-center group">
                  <div className="relative mb-6 sm:mb-8">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-[#e9f638] to-[#d4e032] rounded-3xl flex items-center justify-center mx-auto mb-4 sm:mb-6 shadow-xl group-hover:shadow-2xl transition-all duration-300 group-hover:scale-110">
                      <i className={`${step.icon} text-black text-2xl sm:text-3xl`}></i>
                    </div>
                  </div>
                  <h4 className="text-xl sm:text-2xl font-bold text-black mb-3 sm:mb-4 group-hover:text-[#e9f638] transition-colors duration-300">{step.title}</h4>
                  <p className="text-gray-600 text-base sm:text-lg leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Beach Buggy Image */}
        <section className="py-12 sm:py-16 lg:py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <div className="relative group">
                <img 
                  src="https://static.readdy.ai/image/5c2d16bce98820aab16a3b52f2f5cfd8/0a4b1da33c99933c8f9d0a96932013d1.png" 
                  alt="Beach buggy on Camps Bay beach" 
                  title="Beach buggy rental adventure Camps Bay Twelve Apostles"
                  className="w-full max-w-5xl mx-auto rounded-2xl sm:rounded-3xl shadow-2xl group-hover:shadow-3xl transition-all duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl sm:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 sm:py-20 lg:py-28 bg-gradient-to-br from-[#e9f638] via-[#d4e032] to-[#c5cc2a] relative overflow-hidden">
          <div className="absolute inset-0 bg-black/5"></div>
          <div className="absolute top-10 right-10 w-16 sm:w-32 h-16 sm:h-32 bg-white/10 rounded-full blur-xl"></div>
          <div className="absolute bottom-10 left-10 w-12 sm:w-24 h-12 sm:h-24 bg-white/10 rounded-full blur-xl"></div>
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 sm:mb-8">Ready for Your Beach Adventure?</h3>
            <p className="text-lg sm:text-xl text-white/90 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed">
              Book your beach buggy today and experience the ultimate coastal adventure across South Africa's most beautiful beaches.
            </p>
            <a 
              href="#booking" 
              className="bg-[#e9f638] text-black px-8 sm:px-10 py-4 sm:py-5 rounded-full text-lg sm:text-xl font-bold hover:bg-[#d4e032] transition-all duration-300 whitespace-nowrap cursor-pointer inline-block shadow-2xl hover:shadow-3xl transform hover:-translate-y-1"
            >
              Book Your Bug Now
            </a>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-b from-black to-gray-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16 lg:py-20">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-10 lg:gap-12">
            <div className="col-span-1 sm:col-span-2 lg:col-span-2">
              <div className="flex items-center space-x-3 mb-6 sm:mb-8">
                <img 
                  src="https://static.readdy.ai/image/5c2d16bce98820aab16a3b52f2f5cfd8/71f081b0c967dd005308d1aa03cdf9ab.png"
                  alt="Beach Bugs Logo"
                  className="w-[250px] h-auto object-contain flex-shrink-0"
                />
              </div>
              <p className="text-gray-300 mb-8 sm:mb-10 max-w-md text-base sm:text-lg leading-relaxed">
                Experience unforgettable Camps Bay beach buggy adventures with stunning Twelve Apostles views and comprehensive insurance coverage.
              </p>
              <div className="flex space-x-4 sm:space-x-6">
                <a href="#" className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-800 rounded-xl flex items-center justify-center text-gray-300 hover:text-[#e9f638] hover:bg-gray-700 transition-all duration-300 cursor-pointer" rel="nofollow">
                  <i className="ri-facebook-fill text-lg sm:text-xl"></i>
                </a>
                <a href="#" className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-800 rounded-xl flex items-center justify-center text-gray-300 hover:text-[#e9f638] hover:bg-gray-700 transition-all duration-300 cursor-pointer" rel="nofollow">
                  <i className="ri-instagram-line text-lg sm:text-xl"></i>
                </a>
                <a href="#" className="w-10 h-10 sm:w-12 sm:h-12 bg-gray-800 rounded-xl flex items-center justify-center text-gray-300 hover:text-[#e9f638] hover:bg-gray-700 transition-all duration-300 cursor-pointer" rel="nofollow">
                  <i className="ri-twitter-line text-lg sm:text-xl"></i>
                </a>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-4">Locations</h4>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/bloubergstrand" className="hover:text-white transition-colors">Bloubergstrand</Link></li>
                <li><Link to="/melkbosstrand" className="hover:text-white transition-colors">Melkbosstrand</Link></li>
                <li><Link to="/camps-bay" className="hover:text-white transition-colors">Camps Bay</Link></li>
                <li><Link to="/jeffreys-bay" className="hover:text-white transition-colors">Jeffreys Bay</Link></li>
                <li><Link to="/stilbaai" className="hover:text-white transition-colors">Stilbaai</Link></li>
                <li><Link to="/wilderness" className="hover:text-white transition-colors">Wilderness</Link></li>
                <li><Link to="/george" className="hover:text-white transition-colors">George</Link></li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg sm:text-xl font-bold mb-6 sm:mb-8 text-white">Contact Info</h4>
              <div className="space-y-4 sm:space-y-5 text-gray-300">
                <div className="flex items-center space-x-3">
                  <i className="ri-mail-line text-[#e9f638] text-base sm:text-lg flex-shrink-0"></i>
                  <span className="text-sm sm:text-base break-all">info@beachbugs.co.za</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-map-pin-line text-[#e9f638] text-base sm:text-lg flex-shrink-0"></i>
                  <span className="text-sm sm:text-base">Camps Bay, Cape Town</span>
                </div>
                <div className="flex items-center space-x-3">
                  <i className="ri-time-line text-[#e9f638] text-base sm:text-lg flex-shrink-0"></i>
                  <span className="text-sm sm:text-base">Daily: 8:00-18:00</span>
                </div>
              </div>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 sm:pt-10 mt-12 sm:mt-16 flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
            <p className="text-gray-600 text-xs sm:text-sm text-center sm:text-left">© 2024 Beach Bugs. All rights reserved.</p>
            <div className="flex items-center space-x-4 text-xs sm:text-sm">
              <a href="https://seostudio.co.za/" target="_blank" rel="nofollow noopener noreferrer" className="text-gray-600 hover:text-gray-400 transition-colors cursor-pointer">SEO by SEO Studio</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
